import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';

import api from '../../../../api/axios';
import { IApiResponseError, IApiState } from '../../../../types/api';
import { RootState } from '../../..';
import { ISavingsWithdrawalRecord } from '../types';
import { getApiErrorMessage, getSavingsAuthConfig } from '../utils';

interface ISearchWithdrawalsRequest {
  pageNumber?: number;
  pageSize?: number;
  searchParams?: {
    debitAccount?: string;
    creditAccount?: string;
  };
}

interface ISearchWithdrawalsResponse {
  status?: boolean;
  message?: string;
  totalCount?: number;
  data?: ISavingsWithdrawalRecord[];
  errors?: IApiResponseError[];
}

const initialState: IApiState<ISearchWithdrawalsResponse> = {
  value: null,
  status: 'idle',
  error: '',
};

export const searchWithdrawalsAsync = createAsyncThunk(
  'savings/withdrawals/search',
  async (
    { pageNumber = 1, pageSize = 10, searchParams = {} }: ISearchWithdrawalsRequest,
    { rejectWithValue, getState },
  ) => {
    try {
      const state = getState() as RootState;

      const { data } = await api.post<ISearchWithdrawalsResponse>(
        '/savings/withdrawals/search',
        { pageNumber, pageSize, searchParams },
        getSavingsAuthConfig(state),
      );

      return data;
    } catch (err) {
      const error = err as AxiosError<IApiResponseError>;
      return rejectWithValue(error.response?.data);
    }
  },
);

export const searchWithdrawalsSlice = createSlice({
  name: 'savings/withdrawals/search',
  initialState,
  reducers: {
    resetSearchWithdrawals(state) {
      state.value = null;
      state.status = 'idle';
      state.error = '';
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(searchWithdrawalsAsync.pending, (state) => {
        state.status = 'loading';
        state.error = '';
      })
      .addCase(searchWithdrawalsAsync.fulfilled, (state, action) => {
        state.status = 'success';
        state.value = action.payload as ISearchWithdrawalsResponse;
      })
      .addCase(searchWithdrawalsAsync.rejected, (state, action) => {
        state.status = 'failed';
        state.error = getApiErrorMessage(action.payload);
      });
  },
});

export const { resetSearchWithdrawals } = searchWithdrawalsSlice.actions;

export default searchWithdrawalsSlice.reducer;
